/**
 * 従業員ツリー表示コンポーネント（D&D対応版）
 */
import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import type { EmployeeResponse } from "../../../api/employees";
import type { DepartmentResponse } from "../../../api/departments";
import { AddIcon as PlusIcon, EditIcon, DeleteIcon as TrashIcon } from "../../atoms/Icon";

export interface DepartmentWithEmployees {
    department: DepartmentResponse;
    employees: EmployeeResponse[];
}

export interface EmployeeTreeViewProps {
    storeName: string;
    departmentGroups: DepartmentWithEmployees[];
    unassignedEmployees: EmployeeResponse[];
    onEmployeeClick: (employee: EmployeeResponse) => void;
    onEmployeeDelete: (employee: EmployeeResponse) => void;
    onAddEmployee: () => void;
    className?: string;
}

/** 店舗ノード */
const StoreNode = ({ storeName }: { storeName: string }) => (
    <div className="flex justify-center">
        <div className="px-8 py-3 rounded-lg border-2 border-brand-500 bg-white text-gray-800 font-bold text-sm shadow-sm">{storeName}</div>
    </div>
);

/** 従業員カード (Draggable + ポップオーバー) */
interface EmployeeCardProps {
    employee: EmployeeResponse;
    onEdit: (emp: EmployeeResponse) => void;
    onDelete: (emp: EmployeeResponse) => void;
}

const EmployeeCard = ({ employee, onEdit, onDelete }: EmployeeCardProps) => {
    const [showPopover, setShowPopover] = useState(false);
    const cardRef = useRef<HTMLDivElement | null>(null);

    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: employee.id,
        data: employee,
    });

    useEffect(() => {
        if (!showPopover) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
                setShowPopover(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showPopover]);

    const style = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 100,
    } : undefined;

    return (
        <div
            ref={(node) => { setNodeRef(node); cardRef.current = node; }}
            style={style}
            {...listeners}
            {...attributes}
            className={clsx("relative", isDragging && "opacity-40")}
        >
            <button
                type="button"
                onDoubleClick={() => !isDragging && setShowPopover((prev) => !prev)}
                className={clsx(
                    "w-full px-3 py-2 rounded-lg bg-white border text-left text-sm transition-all",
                    "hover:border-brand-300 hover:shadow-md",
                    "cursor-grab active:cursor-grabbing",
                    showPopover ? "border-brand-400 shadow-md" : "border-gray-200"
                )}
            >
                <div className="font-bold text-gray-800">{employee.name}</div>
                {employee.role && <div className="text-[10px] text-gray-400 mt-0.5 font-medium">{employee.role}</div>}
            </button>

            {showPopover && (
                <div className="absolute left-1/2 -translate-x-1/2 mt-1.5 z-50">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border border-brand-300 shadow-lg">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setShowPopover(false); onEdit(employee); }}
                            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-brand-500 transition-colors"
                        >
                            <EditIcon className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); }}
                            className="p-1.5 rounded-full hover:bg-brand-50 text-gray-400 hover:text-brand-500 transition-colors"
                        >
                            <BuildingIcon className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setShowPopover(false); onDelete(employee); }}
                            className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

/** 部門カラム (Droppable) */
interface DepartmentColumnProps {
    department: { id: number; name: string };
    employees: EmployeeResponse[];
    onEmployeeClick: (employee: EmployeeResponse) => void;
    onEmployeeDelete: (employee: EmployeeResponse) => void;
    onAddEmployee: () => void;
}

const DepartmentColumn = ({ department, employees, onEmployeeClick, onEmployeeDelete, onAddEmployee }: DepartmentColumnProps) => {
    const { setNodeRef, isOver } = useDroppable({ id: department.id });

    return (
        <div
            ref={setNodeRef}
            className={clsx(
                "flex flex-col min-w-[200px] max-w-[240px] rounded-lg border-2 transition-all duration-200",
                isOver ? "border-brand-500 bg-brand-50 scale-105 shadow-lg" : "border-gray-100 bg-white shadow-sm"
            )}
        >
            <div className="px-4 py-3 border-b border-gray-100 text-center font-bold text-sm text-gray-800 bg-gray-50/50 rounded-t-lg">
                {department.name}
            </div>
            <div className="p-3 flex flex-col gap-3 min-h-[100px]">
                {employees.map((emp) => (
                    <EmployeeCard key={emp.id} employee={emp} onEdit={onEmployeeClick} onDelete={onEmployeeDelete} />
                ))}
                <AddEmployeeButton onClick={onAddEmployee} />
            </div>
        </div>
    );
};

// --- 以下、サブコンポーネント群 ---
const AddEmployeeButton = ({ onClick }: { onClick: () => void }) => (
    <button type="button" onClick={onClick} className="w-full py-2 rounded-lg border-2 border-dashed border-brand-200 text-brand-400 text-xs font-bold hover:bg-brand-50 hover:border-brand-400 transition-all flex items-center justify-center gap-1">
        <PlusIcon className="w-3 h-3" />従業員追加
    </button>
);

const ConnectionRail = ({ columnCount }: { columnCount: number }) => {
    if (columnCount === 0) {return null;}
    return (
        <div className="flex justify-center gap-6">
            {Array.from({ length: columnCount }).map((_, index) => {
                const isFirst = index === 0;
                const isLast = index === columnCount - 1;
                const isOnly = columnCount === 1;
                return (
                    <div key={index} className="relative min-w-[200px] max-w-[240px] h-6">
                        {!isOnly && <div className={clsx("absolute top-0 h-0.5 bg-gray-200", isFirst && "left-1/2 right-0", isLast && "left-0 right-1/2", !isFirst && !isLast && "left-0 right-0")} />}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0.5 h-full bg-gray-200" />
                    </div>
                );
            })}
        </div>
    );
};

const EmployeeTreeView = ({ storeName, departmentGroups, unassignedEmployees, onEmployeeClick, onEmployeeDelete, onAddEmployee, className }: EmployeeTreeViewProps) => {
    const allGroups = [
        ...departmentGroups,
        ...(unassignedEmployees.length > 0 ? [{ department: { id: -1, name: "未分類" }, employees: unassignedEmployees }] : []),
    ];

    return (
        <div className={clsx("flex flex-col items-center py-10", className)}>
            <StoreNode storeName={storeName} />
            {allGroups.length > 0 && <div className="w-0.5 h-8 bg-gray-200" />}
            <ConnectionRail columnCount={allGroups.length} />
            <div className="flex justify-center gap-6 items-start">
                {allGroups.map((group) => (
                    <DepartmentColumn key={group.department.id} department={group.department} employees={group.employees} onEmployeeClick={onEmployeeClick} onEmployeeDelete={onEmployeeDelete} onAddEmployee={onAddEmployee} />
                ))}
            </div>
        </div>
    );
};

// --- アイコン ---
const BuildingIcon = (p: React.SVGProps<SVGSVGElement>) => <svg {...p} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" /></svg>;

export default EmployeeTreeView;
